import {
	DefaultExtensionProvider,
	type ExtensionManifest,
	type ExtensionProvider,
} from '@atlaskit/editor-common/extensions';
import { CONNECT_CONFLUENCE_MACRO_EXTENSION_TYPE } from '../utils/constants';
import { type ForgeExtensionManifest, type ForgeExtensionParameters } from '../render';

/**
 * When connect module is migrated to forge, look up for forge extension that matches connect macro key.
 * This is to allow migrated connect macro data to be rendered with forge extension.
 */

type ManifestType = ExtensionManifest<ForgeExtensionParameters> | ForgeExtensionManifest;

export type SendAnalyticsFunc = (extensionKey: string, errorMessage: string) => unknown;

export class ForgeExtensionProvider extends DefaultExtensionProvider<ForgeExtensionParameters> {
	connectExtensionProvider: Promise<ExtensionProvider> | undefined;
	sendAnalytics: SendAnalyticsFunc | undefined;

	constructor(
		manifests: ManifestType[] | Promise<ManifestType[]>,
		connectExtensionProvider?: Promise<ExtensionProvider>,
		sendAnalytics?: SendAnalyticsFunc,
	) {
		super(manifests);
		this.connectExtensionProvider = connectExtensionProvider;
		this.sendAnalytics = sendAnalytics;
	}

	async getExtension(type: string, key: string): Promise<ManifestType> {
		try {
			return await super.getExtension(type, key);
		} catch (forgeError) {
			// There is no Forge app to match the full ARI extensionKey
			// If the extensionType is Connect, we fall back to trying to resolve it by harmonised connect module key
			if (type === CONNECT_CONFLUENCE_MACRO_EXTENSION_TYPE) {
				if (!this.connectExtensionProvider) {
					this.sendAnalytics?.(key, 'No connect extension provider');
				} else {
					try {
						// We first check that no Connect app still exists that can handle this extensionKey
						await (await this.connectExtensionProvider).getExtension(type, key);
					} catch (connectError) {
						// We know that there is no corresponding Connect app, so we can look up the Forge app by Connect key
						const extension = await this.getConnectToForgeExtension(key);
						if (extension) {
							return extension;
						}
						// Report failure to find a Forge extension for this Connect key
						this.sendAnalytics?.(
							key,
							forgeError instanceof Error ? forgeError.message : String(forgeError),
						);
					}
				}
			}
			throw forgeError;
		}
	}

	async getExtensions(): Promise<ManifestType[]> {
		return super.getExtensions();
	}

	async getConnectToForgeExtension(key: string): Promise<ForgeExtensionManifest | undefined> {
		const extensions = await this.getExtensions();
		return extensions.find(
			(manifest: ManifestType): manifest is ForgeExtensionManifest =>
				'connectModuleKey' in manifest &&
				Boolean(manifest.migrationKey) &&
				manifest.connectModuleKey === key,
		);
	}
}
